import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';

// Import logo
import LogoImage from '../assets/LogoFiverFlow.png';
// import ErrorBoundary from './ErrorBoundary'; // Non utilisé pour le moment

import { usePlanRestrictions } from '../hooks/usePlanRestrictions';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import NotificationsDropdown from './NotificationsDropdown';
import CentralizedSearchBar from './CentralizedSearchBar';
import ProfileMenu from './ProfileMenu';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useBodyLock } from '../hooks/useBodyLock';
import { SEOHead } from './SEOHead';
// removed unused imports to satisfy linter without changing behavior

import { 
  Menu, 
  X, 
  LayoutDashboard, 
  Users, 
  Package2, 
  CalendarDays,
  Kanban,
  BarChart, 
  Crown,
  UserCircle,
  Lock,
  CheckCircle2,
  Shield,
  FileText,
  BotMessageSquare,
  ChevronDown,
} from 'lucide-react';

interface LayoutProps {
  children?: React.ReactNode;
}

type SectionKey = 'overview' | 'workspace' | 'assistant' | 'more';

/* ---------- Classes de thème dynamiques ---------- */
// Ces classes utilisent les variables CSS définies par le système de thème
export const pageBgClass = 'theme-layout-bg theme-text-primary';
export const cardClass   = 'rounded-t-lg theme-card-border theme-card-bg theme-card-shadow';
export const subtleBg    = 'theme-subtle-bg';
export const buttonClass = 'theme-btn-secondary rounded-lg border transition-all duration-200';
export const gradientClass = 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9]';

/* ---------- Helper: détecte admin ---------- */
const useIsAdminFromRole = (user: any) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      if (!supabase) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle(); // Utiliser maybeSingle() pour gérer le cas où le profil n'existe pas encore

        if (error) {
          console.warn('Erreur lors de la récupération du rôle (profil peut ne pas exister encore):', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(profile?.role === 'Admin' || profile?.role === 'Moderator');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du rôle admin:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminRole();
  }, [user]);

  return { isAdmin, loading };
};

interface LocalErrorBoundaryProps { children: React.ReactNode }
interface LocalErrorBoundaryState { hasError: boolean; error?: unknown }

class LocalErrorBoundary extends React.Component<LocalErrorBoundaryProps, LocalErrorBoundaryState> {
  state: LocalErrorBoundaryState = { hasError: false };
  static getDerivedStateFromError(error: unknown): LocalErrorBoundaryState {
    return { hasError: true, error };
  }
  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // keep logs for diagnostics without changing UI/UX
    // eslint-disable-next-line no-console
    console.error('[Layout ErrorBoundary] ERREUR DETECTEE:', error);
    // eslint-disable-next-line no-console
    console.error('[Layout ErrorBoundary] Info:', info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 border border-red-300/30 bg-red-900/10 rounded-lg">
          <div className="text-sm text-red-300 font-medium">
            The layout encountered an error. Minimal interface is displayed.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper functions for SEO
const getPageTitle = (pathname: string): string => {
  const titles: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/clients': 'Clients',
    '/orders': 'Orders',
    '/calendar': 'Calendar',
    '/tasks': 'Workboard',
    '/stats': 'Statistics',
    '/invoices': 'Invoices',
    '/assistant': 'AI Assistant',
    '/settings': 'Settings',
    '/upgrade': 'Upgrade',
    '/pricing': 'Pricing',
  };
  return titles[pathname] || 'FiverFlow';
};

const getPageDescription = (pathname: string): string => {
  const descriptions: Record<string, string> = {
    '/dashboard': 'View your freelance business overview, stats, and recent activity',
    '/clients': 'Manage your clients and their information',
    '/orders': 'Track and manage your orders and projects',
    '/calendar': 'Manage your schedule and deadlines',
    '/tasks': 'Organize your tasks and projects',
    '/stats': 'View detailed statistics and analytics',
    '/invoices': 'Create and manage invoices',
    '/assistant': 'Get help from AI Assistant',
    '/settings': 'Manage your account settings',
    '/upgrade': 'Upgrade your FiverFlow plan',
    '/pricing': 'View FiverFlow pricing plans',
  };
  return descriptions[pathname] || 'Manage your freelance business with FiverFlow';
};

const isPrivatePage = (pathname: string): boolean => {
  const privatePages = [
    '/dashboard', '/clients', '/orders', '/calendar', '/tasks',
    '/stats', '/invoices', '/assistant', '/settings', '/profile',
    '/admin', '/project'
  ];
  return privatePages.some(page => pathname.startsWith(page));
};

const LayoutInner: React.FC<LayoutProps> = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [requiredPlan, setRequiredPlan] = useState<'Boost' | 'Scale'>('Boost');
  const [modalOpen, setModalOpen] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    overview: false,
    workspace: false,
    assistant: false,
    more: false,
  });

  // Lock body scroll when mobile drawer is open
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useBodyLock(isSidebarOpen && isMobile);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { restrictions } = usePlanRestrictions();
  
  // Get subscription permissions
  const perms = useSubscriptionPermissions();
  const subscription = perms?.subscription || null;
  const isUserAdmin = perms?.isAdmin || false;
  const permissions = perms?.permissions || null;

  // Get admin status
  const { isAdmin } = useIsAdminFromRole(user);
  const hasAssistantAccess = isUserAdmin || isAdmin || permissions?.assistant === true;
  
  // Fonction pour vérifier l'accès basée sur les permissions
  const checkAccess = (feature: 'calendar' | 'stats' | 'tasks' | 'invoices'): boolean => {
    if (isUserAdmin || isAdmin) return true;
    if (!permissions) return false;
    
    switch (feature) {
      case 'calendar':
        return permissions.calendar || false;
      case 'stats':
        return permissions.stats || false;
      case 'tasks':
        return permissions.workboard || false;
      case 'invoices':
        return permissions.invoices || false;
      default:
        return false;
    }
  };

  /* ---------- NAV STRUCTURE EN 3 SECTIONS + MICRO SECTION BAS ---------- */
  // Section 1: Overview (principales)
  const overviewItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard
      // AUCUN plan requis
    },
    { 
      path: '/calendar', 
      label: 'Calendar', 
      icon: CalendarDays,
      restricted: !checkAccess('calendar') && !restrictions?.isAdmin,
      requiredPlan: 'Boost' as const
    },
    { 
      path: '/stats', 
      label: 'Statistics', 
      icon: BarChart,
      restricted: !checkAccess('stats') && !restrictions?.isAdmin,
      requiredPlan: 'Scale' as const
    },
  ];

  // Section 3: Workspace
  const workspaceItems = [
    { 
      path: '/clients', 
      label: 'Clients', 
      icon: Users
      // AUCUN plan requis
    },
    { 
      path: '/orders',  
      label: 'Orders',  
      icon: Package2
      // AUCUN plan requis
    },
    { 
      path: '/invoices',
      label: 'Invoices',
      icon: FileText,
      restricted: !(restrictions?.isAdmin || checkAccess('invoices')),
      requiredPlan: 'Scale' as const
    },
    { 
      path: '/tasks', 
      label: 'Workboard', 
      icon: Kanban,
      restricted: !checkAccess('tasks') && !restrictions?.isAdmin,
      requiredPlan: 'Boost' as const
    },
  ];

  // Section Assistant AI
  const assistantItems = [
    { 
      path: '/assistant',
      label: 'AI Assistant',
      icon: BotMessageSquare,
      restricted: !hasAssistantAccess,
      requiredPlan: 'Scale' as const
    },
  ];

  // Micro section (tout en bas)
  const bottomItems = [
    { 
      path: '/profile', 
      label: 'Profile', 
      icon: UserCircle
    },
    ...(isAdmin ? [{
      path: '/admin/dashboard',
      label: 'Admin',
      icon: Shield
    }] as const : []),
    { 
      path: '/upgrade', 
      label: 'Upgrade', 
      icon: Crown,
      special: 'upgrade' as const 
    },
  ];


  // theme switcher UI is commented out; drop unused helpers to appease linter

  interface LinkRowProps {
    item: any;
    activeMatcher?: (p: string) => boolean;
  }
  const LinkRow: React.FC<LinkRowProps> = ({ item, activeMatcher }) => {
    // Vérifier que l'icône existe et est valide
    if (!item || !item.icon) {
      console.warn('LinkRow: item or icon is missing', item);
      return null;
    }

    // Vérifier que l'icône est un composant React valide
    const IconComponent = item.icon;
    if (typeof IconComponent !== 'function' && typeof IconComponent !== 'object') {
      console.warn('LinkRow: icon is not a valid React component', item);
      return null;
    }

    const Icon = IconComponent as React.ComponentType<{ size?: number; className?: string }>;
    const isRestricted = 'restricted' in item && (item as any).restricted;
    const isUpgrade = item.special === 'upgrade';
    const isActive =
      activeMatcher
        ? activeMatcher(item.path)
        : (location.pathname === item.path ||
          (item.path?.startsWith?.('/admin') && location.pathname.startsWith('/admin')));

    // 🎨 Style basé sur le thème
    const rowClass = isActive
      ? `theme-sidebar-text-active theme-hover-button rounded-lg font-medium`
      : `theme-sidebar-text hover:theme-sidebar-text-hover theme-hover-button rounded-lg transition-all duration-200`;

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          if (isRestricted) {
            e.preventDefault();
            const plan = (item as any).requiredPlan || 'Boost';
            setRequiredPlan(plan);
            setModalOpen(true);
            setUpgradeOpen(true);
            return;
          }
          setIsSidebarOpen(false);
        }}
        className={`group relative flex items-center gap-3 px-4 py-3 mx-2 text-sm font-medium transition-all duration-200 ${rowClass}`}
      >
        {/* Icône basée sur le thème */}
        <div className="flex items-center justify-center w-5 h-5">
          {React.isValidElement(IconComponent) ? (
            IconComponent
          ) : (
            <Icon size={18} className={isActive ? 'theme-sidebar-icon-active' : 'theme-sidebar-icon'} />
          )}
        </div>

        <span className="truncate font-medium">{item.label}</span>

        {isRestricted && 'requiredPlan' in item && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full theme-btn-secondary border font-medium">
            {(item as any).requiredPlan}
          </span>
        )}

        {isUpgrade && (
          <div className="ml-auto">
            <div className="w-2 h-2 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] rounded-full animate-pulse" />
          </div>
        )}
      </Link>
    );
  };

  const toggleSection = (section: SectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderSection = (
    key: SectionKey,
    label: string,
    items: typeof overviewItems,
    options?: {
      containerClassName?: string;
      activeMatcher?: (item: any) => ((p: string) => boolean) | undefined;
    }
  ) => (
    <>
      <div
        className={`px-3 sm:px-4 mb-2 ${options?.containerClassName ?? ''}`}
      >
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className="w-full flex items-center justify-between gap-2 text-xs font-semibold theme-sidebar-section-label uppercase tracking-wider cursor-pointer select-none"
        >
          <span className="text-left">{label}</span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${collapsedSections[key] ? '-rotate-90' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {!collapsedSections[key] && (
        <div className="space-y-1">
          {items.map((item) => (
            <LinkRow
              key={item.path}
              item={item}
              activeMatcher={options?.activeMatcher?.(item)}
            />
          ))}
        </div>
      )}
    </>
  );

  const bottomSectionMatcher = (p: string) =>
    p === '/profile'
      ? location.pathname === '/profile'
      : p.startsWith('/admin')
      ? location.pathname.startsWith('/admin')
      : location.pathname === p;

  return (
    <>
      <SEOHead 
        title={`${getPageTitle(location.pathname)} - FiverFlow`}
        description={getPageDescription(location.pathname)}
        canonical={location.pathname}
        noindex={isPrivatePage(location.pathname)}
      />
      <div className="min-h-screen theme-layout-bg theme-text-primary antialiased no-page-overflow">
      {/* Topbar - Style basé sur le thème - Fixed for mobile */}
      <header className="fixed top-0 inset-x-0 z-40 h-[var(--header-h)] theme-nav-bg theme-nav-bg-soft theme-nav-text backdrop-blur border-b theme-nav-border shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-6 h-full">
          {/* Left section - Logo, separator, and Search bar */}
          <div className="flex items-center flex-1">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg theme-nav-icon hover:theme-nav-icon-hover theme-hover-button transition-all duration-200 mr-4"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={20} />}
            </button>
            <Link to="/dashboard" className="text-2xl font-extrabold leading-none mr-4">
              <div className="flex items-center space-x-3">
                <img src={LogoImage} alt="Logo" className="h-6 w-auto" />
              </div>
            </Link>
            
            {/* Diagonal separator */}
            <div className="hidden md:block w-px h-6 bg-[var(--theme-nav-border)] rotate-12 mr-4" />
            
            {/* Search bar */}
            <div className="hidden md:flex flex-1">
              <CentralizedSearchBar />
            </div>
          </div>
          
          {/* Right section - Theme, Notifications, logout */}
          <div className="flex items-center gap-2 sm:gap-3 pr-4">
            {/* Theme changer moved to settings */}
            {/* <button 
              onClick={handleThemeChange}
              className="size-10 rounded-lg grid place-items-center text-[rgb(var(--brand-contrast))] bg-[rgb(var(--elevated))] hover:bg-[rgb(var(--card))] border border-[rgb(var(--border))] transition-all duration-200"
              title={`Current theme: ${currentTheme}. Click to cycle through themes.`}
            >
              {React.createElement(getThemeIcon(), { size: 18 })}
            </button> */}

            <LocalErrorBoundary>
              <div className="size-10 rounded-lg grid place-items-center transition-all duration-200 hover:theme-hover-button theme-nav-icon cursor-pointer">
                <NotificationsDropdown />
              </div>
            </LocalErrorBoundary>

          {/* Upgrade button - Hidden on mobile, visible on desktop */}
          <button
            onClick={() => navigate('/upgrade')}
            className="hidden md:inline-flex items-center justify-center h-10 px-4 md:px-5 rounded-lg text-sm md:text-base font-semibold text-black bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 hover:from-yellow-300 hover:via-amber-300 hover:to-yellow-400 shadow-[0_8px_24px_rgba(245,158,11,0.35)] transition-all duration-200 min-h-[44px]"
            title="Upgrade"
          >
            <Crown size={16} className="mr-2" />
            <span>Upgrade</span>
          </button>

          {/* Profile Menu */}
          <ProfileMenu />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Style basé sur le thème - Fixed for mobile */}
        <aside
          className={`
            hidden lg:block fixed top-[var(--header-h)] left-0 h-[calc(100vh-var(--header-h))] w-[var(--sidebar-w)] z-40
            theme-sidebar-bg
            border-r theme-sidebar-border
            shadow-sm
          `}
        >
          <nav className="h-full px-3 sm:px-4 py-5 space-y-2 overflow-y-auto scrollbar-hide">
            <LocalErrorBoundary>
              {renderSection('overview', 'Overview', overviewItems)}
              {renderSection('workspace', 'Workspace', workspaceItems, { containerClassName: 'pt-4' })}
              {renderSection('assistant', 'AI Assistant', assistantItems, { containerClassName: 'pt-4' })}
              {renderSection('more', 'More', bottomItems, {
                containerClassName: 'pt-4 mt-2',
                activeMatcher: () => bottomSectionMatcher,
              })}
            </LocalErrorBoundary>
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        {isSidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-30 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
              aria-hidden="true"
            />
            <aside
              className={`
                fixed top-[var(--header-h)] left-0 bottom-0 z-40 w-64
                theme-sidebar-bg
                border-r theme-sidebar-border
                shadow-sm
                lg:hidden
              `}
            >
              <nav className="h-full px-3 sm:px-4 py-5 space-y-2 overflow-y-auto scrollbar-hide">
                <LocalErrorBoundary>
                  {renderSection('overview', 'Overview', overviewItems)}
                  {renderSection('workspace', 'Workspace', workspaceItems, { containerClassName: 'pt-4' })}
                  {renderSection('assistant', 'AI Assistant', assistantItems, { containerClassName: 'pt-4' })}
                  {renderSection('more', 'More', bottomItems, {
                    containerClassName: 'pt-4 mt-2',
                    activeMatcher: () => bottomSectionMatcher,
                  })}
                </LocalErrorBoundary>
              </nav>
            </aside>
          </>
        )}

        <main className="flex-1 pt-[var(--header-h)] lg:pl-[var(--sidebar-w)] min-h-screen theme-layout-bg theme-text-primary antialiased">
          <div className="page-container py-4 sm:py-6 lg:py-8 h-full lg:h-[calc(100vh-var(--header-h))]">
            <LocalErrorBoundary><Outlet /></LocalErrorBoundary>
          </div>
        </main>
        
      </div>

      {upgradeOpen && modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setModalOpen(false);
              setUpgradeOpen(false);
              navigate('/dashboard');
            }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            onClick={(e) => e.stopPropagation()}
            className="
              relative w-full max-w-lg
              rounded-3xl overflow-hidden
              shadow-2xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--elevated))]/90
              backdrop-blur-xl
            "
          >
            <div className="h-1 w-full bg-[rgb(var(--brand))]" />

            <div className="px-6 pt-6 pb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="
                    p-2.5 rounded-2xl
                    bg-[rgb(var(--brand))]
                    text-[rgb(var(--brand-contrast))] shadow-md shadow-[rgb(var(--brand))]/20
                  "
                  aria-hidden="true"
                >
                  <Crown size={20} />
                </div>
                <div>
                  <h3 id="upgrade-modal-title" className="text-xl font-semibold text-[rgb(var(--fg))]">
                    Upgrade required
                  </h3>
                  <p className="mt-0.5 text-sm text-[rgb(var(--muted))]">
                    Unlock this feature on the <span className="font-medium">{requiredPlan}</span> plan.
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setModalOpen(false);
                  setUpgradeOpen(false);
                  navigate('/dashboard');
                }}
                className="p-2 rounded-lg hover:bg-[rgb(var(--subtle))] text-[rgb(var(--muted))]"
                aria-label="Close upgrade modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 pb-6">
              <div className="mb-4">
                <span
                  className="
                    inline-flex items-center gap-1.5
                    text-xs font-medium
                    px-2.5 py-1.5 rounded-full
                    bg-[rgb(var(--brand))] text-[rgb(var(--brand-contrast))]
                    shadow-md shadow-[rgb(var(--brand))]/10
                  "
                >
                  <Lock size={14} />
                  {requiredPlan} features
                </span>
              </div>

              <div
                className="
                  rounded-2xl p-4
                  border border-[rgb(var(--border))]
                  bg-[rgb(var(--card))]
                "
              >
                <p className="text-sm mb-3 text-[rgb(var(--fg))]">
                  You'll get:
                </p>

                {requiredPlan === "Boost" ? (
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Unlimited clients & orders
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Dashboard, Clients, Orders
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Calendar access
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Workboard access
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Priority support
                    </li>
                  </ul>
                ) : (
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Everything in Boost
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Advanced statistics
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Advanced invoices
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      AI Assistant
                    </li>
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Priority support
                    </li>
                  </ul>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2.5">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setUpgradeOpen(false);
                    navigate('/dashboard');
                  }}
                  className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--fg))] hover:bg-[rgb(var(--subtle))] transition"
                >
                  Not now
                </button>
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setUpgradeOpen(false);
                    navigate('/upgrade');
                  }}
                  className="px-4 py-2 rounded-xl text-[rgb(var(--brand-contrast))] bg-[rgb(var(--brand))] hover:opacity-90 focus:ring-2 focus:ring-[rgb(var(--brand))]/50 shadow-md hover:shadow-lg hover:shadow-[rgb(var(--brand))]/20 transition"
                >
                  See plans
                </button>
              </div>

              <p className="mt-3 text-center text-xs text-[rgb(var(--muted))]">
                You can change or cancel your plan anytime.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* 🔥 AUTHENTIFICATION SUPPRIMÉE - Debug panel supprimé */}
    </div>
    <Analytics />
    </>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  
  // Skip Layout for landing page only
  if (location.pathname === '/landing') {
    return <>{children || <Outlet />}</>;
  }
  
  return (
    <LocalErrorBoundary>
      <LayoutInner />
    </LocalErrorBoundary>
  );
};

export default Layout;
